import { Component, OnInit, ViewChild, AfterViewChecked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Store } from '@ngrx/store';
import {
  saveData,
  updateCaptchaToken,
  updateError,
  updatePopover,
  updateSaving,
  updateUser
} from './state/register_actions_creator';
import { RegisterState, initialRegisterState } from './state/register_state';
import { WindowService } from '../../shared/services/window.service';
import {
  MAX_DISPLAY_NAME_LENGTH, MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH,
  STRONG_PASSWORD_PATTERN
} from '../shared/constants';
import { ENV } from '../../environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { DEFAULT_INTENT, PRODUCT_INTENT } from '../platform/state/product_intent';
import { FlowTrackingService } from '../shared/services/flow-tracking.service';
import { IAppState } from '../../app_state';
import { AnalyticsTrackingProvider } from '@yotpo-ui/core';

@Component({
  selector: 'yi-register',
  template: require('./register.component.html'),
  styles: [require('./register.component.scss')],

})
export class RegisterComponent implements OnInit, AfterViewChecked {
  popoverPlacement = this.windowService.isNarrowWindow() ? 'top' : 'right';
  nameMaxLength = MAX_DISPLAY_NAME_LENGTH;
  passwordMinLength = MIN_PASSWORD_LENGTH;
  passwordMaxLength = MAX_PASSWORD_LENGTH;
  passwordPattern = STRONG_PASSWORD_PATTERN;
  reCaptchaSiteKey = ENV.ReCaptchaSiteKey;
  showCaptcha = ENV.ShowCaptcha;
  userForm: NgForm;
  @ViewChild('userForm') currentForm: NgForm;
  @ViewChild('invalidNamePop') invalidNamePop: PopoverDirective;
  @ViewChild('invalidEmailPop') invalidEmailPop: PopoverDirective;
  @ViewChild('invalidPasswordPop') invalidPasswordPop: PopoverDirective;
  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  private validationMessages = {
    'name': {
      'required': '',
      'minlength': '',
      'maxlength': ''
    },
    'email': {
      'required': '',
      'email': ''
    },
    'password': {
      'required': '',
      'minlength': '',
      'maxlength': '',
      'pattern': ''
    }
  };

  private user;
  private error;
  private popover;
  private captchaToken;
  saving;
  formTouched;
  private shouldTriggerSaveDataEvent = true;

  translateValidationMessages() {
    this.translateService.stream('SIGNUP.REGISTER.VALIDATION.NAME').subscribe(
      (translated) => {
        this.validationMessages.name.required = translated['REQUIRED'];
        this.validationMessages.name.minlength = translated['MIN'];
        this.validationMessages.name.maxlength = translated['MAX'];
      });
    this.translateService.stream('SIGNUP.REGISTER.VALIDATION.EMAIL').subscribe(
      (translated) => {
        this.validationMessages.email.required = translated['REQUIRED'];
        this.validationMessages.email.email = translated['EMAIL'];
      });
    this.translateService.stream('SIGNUP.REGISTER.VALIDATION.PASSWORD').subscribe(
      (translated) => {
        this.validationMessages.password.required = translated['REQUIRED'];
        this.validationMessages.password.minlength = translated['MIN'];
        this.validationMessages.password.maxlength = translated['MAX'];
        this.validationMessages.password.pattern = translated['WEAK'];
      });
  }

  constructor(
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private redCrossService: AnalyticsTrackingProvider,
    private windowService: WindowService,
    private store: Store<IAppState>,
    private flowTrackingService: FlowTrackingService
  ) {
    this.translateValidationMessages();
    this.formTouched = false;
    this.store.select(state => state.app.signup.register).subscribe((data: any) => {
      this.user = data.user;
      this.error = data.error;
      this.popover = data.popover;
      this.saving = data.saving;
      if (this.popover) {
        if (this.showCaptcha && Object.keys(this.error).some(error_key => this.error[error_key])) {
          this.shouldTriggerSaveDataEvent = false;
          this.captchaRef.reset();
        }
        this.showPopovers();
      } else {
        this.hidePopovers();
      }
    });
  }

  executeCaptcha() {
    if (this.showCaptcha) {
      this.shouldTriggerSaveDataEvent = true;
      this.captchaRef.execute();
    }
  }

  captchaResolved(captchaToken: string) {
    this.captchaToken = captchaToken;
    this.store.dispatch(updateCaptchaToken({captchaToken}));
    if (this.currentForm.valid && this.shouldTriggerSaveDataEvent) {
      this.store.dispatch(saveData());
    }
  }

  ngOnInit() {
    this.redCrossService.page('B2B', {pageCategory: 'Registration'});
    this.activatedRoute.queryParams.subscribe(params => {
      let registerParams: RegisterState = initialRegisterState;
      let productIntent;
      if (params['product']) {
        productIntent = PRODUCT_INTENT[params['product'].toLowerCase()];
      } else if (ENV.UseAccountSystemAsDefault) {
        productIntent = DEFAULT_INTENT;
      }
      registerParams = {...registerParams, user: {...registerParams.user,
          name: params['name'],
          email: params['email'],
          not_finished_registration: true,
          productIntent: productIntent,
          use_account_system: productIntent ? true : false,
          campaign_params: {...registerParams.user.campaign_params,
            utm_campaign: params['utm_campaign'],
            utm_medium: params['utm_medium'],
            utm_source: params['utm_source']
          }
        }};
      this.store.dispatch(updateUser({user: registerParams.user}));
    });
    this.flowTrackingService.StepOneStarted(this.activatedRoute.snapshot);
  }

  onSubmit(form: NgForm) {
    this.onValueChanged();
    let valid_form = form.valid;
    this.store.dispatch(updateSaving({saving: true}));

    if (!valid_form || this.isErrorsExists()) {
      this.store.dispatch(updateSaving({saving: false}));
      this.store.dispatch(updatePopover({popover: true}));
    } else if (this.captchaToken || this.showCaptcha === false) {
      this.store.dispatch(saveData());
    }
  }

  ngAfterViewChecked() {
    if (this.currentForm === this.userForm) { return; }
    this.userForm = this.currentForm;
    if (this.userForm) {
      this.userForm.valueChanges
        .subscribe(_ => {
          this.onValueChanged();
          this.store.dispatch(updatePopover({popover: false}));
        });
    }
  }

  onValueChanged() {
    if (!this.userForm) { return; }
    const form = this.userForm.form;
    this.formTouched = form.touched;
    // clear previous error message (if any)
    let error = {name: '', email: '', password: ''};
    for (const field of Object.keys(error)) {
      const control = form.get(field);
      const messages = this.validationMessages[field];
      if (control && !control.valid) {
        for (const key of Object.keys(control.errors)) {
          error[field] = messages[key];
        }
      }
      if (field === 'name' && this.isMaxNameLength()) {
        error[field] = messages['maxlength'];
      }
    }
    this.store.dispatch(updateError({error}));
  }

  isMaxNameLength() {
    return this.user.name && this.user.name.length >= this.nameMaxLength;
  }

  isErrorsExists() {
    for ( let field of Object.keys(this.error)) {
      if ( this.error[field] !== '') {
        return true;
      }
    }
    return false;
  }

  hidePopovers() {
    if (this.invalidNamePop && this.invalidEmailPop && this.invalidPasswordPop) {
      this.invalidNamePop.hide();
      this.invalidEmailPop.hide();
      this.invalidPasswordPop.hide();
    }
  }

  showPopovers() {
    if (this.error['name']) {
      this.invalidNamePop.show();
    } else {
      this.invalidNamePop.hide();
    }
    if (this.error['email']) {
      this.invalidEmailPop.show();
    } else {
      this.invalidEmailPop.hide();
    }
    if (this.error['password']) {
      this.invalidPasswordPop.show();
    } else {
      this.invalidPasswordPop.hide();
    }
  }

  updateEmail(email) {
    this.store.dispatch(updateUser({user: {...this.user, email}}));
  }

  updateName(name) {
    this.store.dispatch(updateUser({user: {...this.user, name}}));
  }

  updatePassword(password) {
    this.store.dispatch(updateUser({user: {...this.user, password}}));
  }
}
